import React from 'react';
import { Control, Controller, FieldValues, Path, PathValue, useFieldArray, ArrayPath } from 'react-hook-form';
import { Plus } from 'lucide-react';

import { Button } from './button';
import { cn } from 'utils/tw';

export interface ArrayFieldItemProps<T = any> {
  field: {
    name: string;
    value: T;
    onChange: (value: T) => void;
    onBlur: () => void;
  };
  index: number;
  onRemove: () => void;
}

export interface ArrayFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends ArrayPath<TFieldValues> = ArrayPath<TFieldValues>
> {
  name: TName;
  control: Control<TFieldValues>;
  label?: string;
  defaultItem: PathValue<TFieldValues, TName>[0];
  renderItem: (props: ArrayFieldItemProps<PathValue<TFieldValues, TName>[0]>) => React.ReactElement;
  addButtonText?: string;
  maxItems?: number;
  className?: string;
  errorText?: string;
}

export function ArrayField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends ArrayPath<TFieldValues> = ArrayPath<TFieldValues>
>({
  name,
  control,
  label,
  defaultItem,
  renderItem,
  addButtonText = "Add New",
  maxItems,
  className,
  errorText
}: ArrayFieldProps<TFieldValues, TName>) {
  const { fields, append, remove } = useFieldArray({
    control,
    name
  });

  const handleAdd = () => {
    if (!maxItems || fields.length < maxItems) {
      append(defaultItem);
    }
  };

  const handleRemove = (index: number) => {
    remove(index);
  };

  return (
    <div className={cn("flex flex-col gap-sm flex-1", className)}>
      {label && (
        <label className='font-medium text-small max-w-max flex text-gray-900 relative' htmlFor={name + '.0'}>
          {label}
        </label>
      )}
      
      <div className="flex flex-col gap-4">
        {fields.map((field, index) => {
          return (
            <div key={field.id} className="relative">
              <Controller
                name={`${name}.${index}` as Path<TFieldValues>}
                control={control}
                render={({ field: controllerField }) => 
                  renderItem({
                    field: controllerField,
                    index,
                    onRemove: () => handleRemove(index),
                  })
                }
              />
            </div>
          );
        })}
        
        {(!maxItems || fields.length < maxItems) && (
          <Button
            type="button"
            variant="nakedBrandLink"
            size="small"
            onClick={handleAdd}
            className="self-start pl-0"
          >
            <Plus size={16} />
            {addButtonText}
          </Button>
        )}
      </div>
      
      {errorText && (
        <p className="text-small text-error-600">{errorText}</p>
      )}
    </div>
  );
}